import { z } from "zod";
import { IsoDateTimeSchema } from "./primitives.js";
import { MemoryCandidateSchema } from "./memory.js";
import { DayLedgerResponseSchema } from "./ledger.js";
export const MealOutcomeStatusSchema = z.enum(["had", "partial", "skipped", "changed"]);
// POST /v1/meals/{id}/outcomes request. The base object stays exported for
// schema composition (tools.ts RecordFeedbackInputSchema extends it).
export const MealOutcomeRequestBaseSchema = z.object({
    outcome: MealOutcomeStatusSchema,
    actualServings: z.number().positive().optional(),
    actualTimeMinutes: z.number().int().nonnegative().optional(),
    rating: z.number().int().min(1).max(5).optional(),
    feedbackText: z.string().optional(),
});
/** 2026-09-02 acceptance: "partly ate it" counts ONLY what was actually
 * eaten — the amount must be captured, never invented (no 50% default). */
export const partialRequiresActualServings = (r) => r.outcome !== "partial" || r.actualServings !== undefined;
export const MealOutcomeRequestSchema = MealOutcomeRequestBaseSchema.refine(partialRequiresActualServings, {
    message: "actualServings is required when outcome is 'partial'",
    path: ["actualServings"],
});
/**
 * Truthful summary of the deterministic pantry sync a meal outcome triggered
 * (Pantry batch 2026-08 cooperation loop): marking "had" deducts the meal's
 * scaled recipe ingredients from matching, certain-quantity pantry items
 * (floored at 0); changing the outcome away from "had" restores exactly the
 * previously recorded deltas. Counts are DISTINCT ingredients actually
 * touched — 0 when nothing in the pantry matched, so the UI can stay honest
 * and show no toast at all.
 */
/** ADR-076: one Kitchen movement line, in the ingredient's canonical unit. */
export const PantrySyncLineSchema = z.object({
    ingredientId: z.string(),
    quantity: z.number().nonnegative(),
    unit: z.enum(["g", "ml", "count"]),
});
/** ADR-076: why a required ingredient moved nothing — reported, never silent. */
export const PantrySyncSkipReasonSchema = z.enum([
    "not_in_kitchen",
    "uncertain_quantity",
    "unit_unconvertible",
    /** ADR-076 correctness patch: every eligible lot found for this ingredient has expired (its expiry is before today) — never consumed. */
    "expired",
    /** Reversal (away from "had"): the user edited that Kitchen item after the deduction — their number stands, nothing is put back. */
    "manually_adjusted",
    /** Reversal: the Kitchen item is no longer available (discarded/consumed since) — nothing to put back into. */
    "no_longer_available",
    "unknown_ingredient",
]);
export const MealOutcomePantrySyncSchema = z.object({
    deductedIngredients: z.number().int().nonnegative(),
    restoredIngredients: z.number().int().nonnegative(),
    /**
     * ADR-076 post-meal consumption truth (additive): exactly what moved and
     * what could not — so Amy and the clients can say "deducted 250 g rice"
     * or "the recipe needed 250 g rice but only 100 g was there" from
     * evidence. Absent on older responses; counts above stay authoritative.
     */
    deducted: z.array(PantrySyncLineSchema).optional(),
    restored: z.array(PantrySyncLineSchema).optional(),
    skipped: z.array(z.object({ ingredientId: z.string(), reason: PantrySyncSkipReasonSchema })).optional(),
    shortfalls: z
        .array(z.object({
        ingredientId: z.string(),
        required: z.number().nonnegative(),
        available: z.number().nonnegative(),
        unit: z.enum(["g", "ml", "count"]),
    }))
        .optional(),
    /**
     * ADR-065: present when the meal drew from PREPARED food instead of raw
     * ingredients — the portions this outcome took (or gave back) and what is
     * left of that batch. Raw counts are then 0: the ingredients were already
     * spent when the batch was cooked, and are never deducted twice.
     */
    prepared: z
        .object({
        preparedBatchId: z.string().nullable(),
        consumedPortions: z.number().nonnegative(),
        restoredPortions: z.number().nonnegative(),
        portionsRemaining: z.number().nonnegative().nullable(),
    })
        .optional(),
});
export const MealOutcomeResponseSchema = z.object({
    id: z.string(),
    mealId: z.string(),
    outcome: MealOutcomeStatusSchema,
    recordedAt: IsoDateTimeSchema,
    /** Populated when the feedback text/rating warrants a learnable-preference proposal. */
    memoryCandidate: MemoryCandidateSchema.nullable(),
    /**
     * Optional (not nullable-required) so idempotency replays stored before
     * this field existed still parse.
     */
    pantrySync: MealOutcomePantrySyncSchema.nullable().optional(),
    /**
     * Step 3 (whole-day protection): the freshly recomputed day ledger when
     * the outcome is for TODAY — the after-eating twin of
     * POST /v1/ledger/preview, so no consumer runs a second calculation.
     * Optional: absent on idempotency replays, backfilled days, and wiring
     * without ledger deps.
     */
    ledger: DayLedgerResponseSchema.optional(),
});
//# sourceMappingURL=meals.js.map